import React, { useEffect, useMemo, useState } from "react"
import { Input, InputNumber, Radio, Select, Switch } from "antd"
import TableStyleControl from "shared/ui/TableStyle/TableStyleControl.component"
import {
  XTAB_STATISTIC_OPTIONS,
  DTAB_STATISTIC_OPTIONS,
} from "shared/ui/Builder"
import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import { SIGNIFICANCE_TESTED_STATISTICS } from "types/significance.types"
import type { AxisLeaf, TableModel } from "utils/tableModel"
import {
  flattenCodebookForSelect,
  getBoolChildLabels,
} from "utils/codebookUtils"
import {
  BarTile,
  ChartOptions,
  DASHBOARD_TABLE_STYLE,
  DataTile,
  ImageTile,
  MAX_PANELS,
  PieTile,
  RepeatBy,
  ScorecardTile,
  Tile,
  UNIVERSE_TOKEN,
  isDataTile,
} from "features/Dashboard/types/dashboardDocument"
import {
  runtimeModel,
  useDashboardStore,
} from "features/Dashboard/runtime/dashboardStore"
import { leafLabel } from "features/Dashboard/runtime/modelAdapters"
import { displayRows } from "features/Dashboard/runtime/categoryDisplay"
import {
  canRepeat,
  repeatCategories,
  RepeatTile,
} from "features/Dashboard/runtime/panels"
import CategoryDisplayEditor from "./CategoryDisplayEditor.component"

type VisualOf<K extends DataTile["type"]> = Extract<
  DataTile,
  { type: K }
>["visual"]

const ORIENTATION_OPTIONS: {
  value: BarTile["visual"]["orientation"]
  label: string
}[] = [
  { value: "vertical", label: "Vertical" },
  { value: "horizontal", label: "Horizontal" },
]
const BAR_MODE_OPTIONS: { value: BarTile["visual"]["mode"]; label: string }[] =
  [
    { value: "grouped", label: "Grouped" },
    { value: "stacked", label: "Stacked" },
  ]
const SERIES_AXIS_OPTIONS: {
  value: BarTile["visual"]["seriesAxis"]
  label: string
}[] = [
  { value: "across", label: "Columns" },
  { value: "down", label: "Rows" },
]
const LEGEND_OPTIONS: { value: PieTile["visual"]["legend"]; label: string }[] =
  [
    { value: "right", label: "Right" },
    { value: "bottom", label: "Bottom" },
    { value: "none", label: "None" },
  ]
const FIT_OPTIONS: { value: ImageTile["fit"]; label: string }[] = [
  { value: "contain", label: "Contain" },
  { value: "cover", label: "Cover" },
]
const SORT_OPTIONS: { value: ChartOptions["sort"]; label: string }[] = [
  { value: "none", label: "Table order" },
  { value: "desc", label: "Largest first" },
  { value: "asc", label: "Smallest first" },
]
const COMPARE_OPTIONS: {
  value: ScorecardTile["visual"]["compare"]
  label: string
}[] = [
  { value: "previous", label: "Previous column" },
  { value: "target", label: "Target" },
  { value: "none", label: "None" },
]
const GOOD_OPTIONS: {
  value: ScorecardTile["visual"]["goodDirection"]
  label: string
}[] = [
  { value: "up", label: "Higher is better" },
  { value: "down", label: "Lower is better" },
]
const CHART_LEGEND_OPTIONS: {
  value: ChartOptions["legend"]
  label: string
}[] = [
  { value: "bottom", label: "Bottom" },
  { value: "right", label: "Right" },
  { value: "none", label: "None" },
]

/**
 * The categories of one axis of the last result: the statistic axis repeats
 * each category once per statistic, so ids are deduped; Totals are left out.
 */
const categoryLeaves = (leaves: AxisLeaf[] | undefined): AxisLeaf[] =>
  (leaves ?? []).filter(
    (l, i, arr) => !l.isTotal && arr.findIndex((o) => o.id === l.id) === i
  )
const leafOptions = (leaves: AxisLeaf[]) =>
  leaves.map((l) => ({ value: l.id, label: leafLabel(l) }))

/**
 * The rows a pick on the down axis can name, as the tile shows them: the
 * categories of the last result (all of them, so a category hidden from
 * display stays listed) unless the tile shows only its measures, then its
 * measures unless it shows only its categories (see `displayRows`).
 */
const downRowOptions = (tile: Tile, model: TableModel | undefined) =>
  isDataTile(tile)
    ? displayRows(tile, model).map((r) => ({ value: r.id, label: r.label }))
    : []

/** `options` with `patch` applied; a field patched to undefined is removed. */
const patchChart = (
  options: ChartOptions,
  patch: Partial<ChartOptions>
): ChartOptions => {
  const next: Record<string, unknown> = { ...options, ...patch }
  Object.keys(next).forEach((k) => {
    if (next[k] === undefined) delete next[k]
  })
  return next as unknown as ChartOptions
}

/** `visual` with `patch` applied; a field patched to undefined is removed. */
const patchScorecard = (
  visual: ScorecardTile["visual"],
  patch: Partial<ScorecardTile["visual"]>
): ScorecardTile["visual"] => {
  const next: Record<string, unknown> = { ...visual, ...patch }
  Object.keys(next).forEach((k) => {
    if (next[k] === undefined) delete next[k]
  })
  return next as unknown as ScorecardTile["visual"]
}

/** A number typed into a field, or undefined when the field is empty. */
const typed = (v: number | string | null): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined

/**
 * The chart options of a bar, line or pie tile: value labels, sort (bar
 * and pie), and for bar and line the legend, a target line and fixed axis
 * ends; stacked bars can scale each stack to 100%. Numbers are typed in
 * the chart's units.
 */
const ChartSection: React.FC<{
  tile: Extract<DataTile, { type: "bar" | "line" | "pie" }>
  onChange: (patch: Partial<ChartOptions>) => void
}> = ({ tile, onChange }) => {
  const chart = tile.chart
  const cartesian = tile.type !== "pie"
  const stacked = tile.type === "bar" && tile.visual.mode === "stacked"
  return (
    <>
      <h4 className="dq-inspector__h">Chart</h4>
      <label className="dq-field dq-field--row">
        <span>Data labels</span>
        <Switch
          size="small"
          checked={chart.dataLabels === "on"}
          onChange={(on) => onChange({ dataLabels: on ? "on" : "off" })}
        />
      </label>
      {tile.type !== "line" && (
        <label className="dq-field">
          <span>Sort</span>
          <Select
            size="small"
            value={chart.sort}
            options={SORT_OPTIONS}
            onChange={(sort) => onChange({ sort })}
          />
        </label>
      )}
      {cartesian && (
        <label className="dq-field">
          <span>Legend</span>
          <Select
            size="small"
            value={chart.legend}
            options={CHART_LEGEND_OPTIONS}
            onChange={(legend) => onChange({ legend })}
          />
        </label>
      )}
      {tile.type === "bar" && (
        <div className="dq-toggle">
          <label className="dq-field dq-field--row">
            <span>Stack to 100%</span>
            <Switch
              size="small"
              checked={chart.normalize}
              disabled={!stacked}
              onChange={(normalize) => onChange({ normalize })}
            />
          </label>
          {!stacked && (
            <p className="dq-field__hint">For stacked bars.</p>
          )}
        </div>
      )}
      {cartesian && (
        <>
          <div className="dq-field">
            <span>Target line</span>
            <div className="dq-field__pair">
              <InputNumber
                size="small"
                aria-label="Target value"
                placeholder="None"
                value={chart.target?.value ?? null}
                onChange={(v) => {
                  const value = typed(v)
                  onChange({
                    target:
                      value === undefined
                        ? undefined
                        : { value, label: chart.target?.label ?? "" },
                  })
                }}
              />
              <Input
                size="small"
                aria-label="Target label"
                placeholder="Target"
                value={chart.target?.label ?? ""}
                disabled={!chart.target}
                onChange={(e) =>
                  chart.target &&
                  onChange({
                    target: { ...chart.target, label: e.target.value },
                  })
                }
              />
            </div>
          </div>
          <AxisRange
            key={tile.id}
            min={chart.axisMin}
            max={chart.axisMax}
            onChange={onChange}
          />
        </>
      )}
    </>
  )
}

export const AXIS_RANGE_ERROR = "To must be above From"

/**
 * The value axis ends, From and To, in the chart's units. A pair whose To
 * is at or below From is marked invalid and not written; the chart keeps
 * the ends it had until the pair is fixed. Either end alone is fine.
 */
const AxisRange: React.FC<{
  min: number | undefined
  max: number | undefined
  onChange: (patch: Partial<ChartOptions>) => void
}> = ({ min, max, onChange }) => {
  const [from, setFrom] = useState(min)
  const [to, setTo] = useState(max)
  // Ends written from elsewhere replace the fields.
  useEffect(() => {
    setFrom(min)
    setTo(max)
  }, [min, max])
  const invalid = from !== undefined && to !== undefined && to <= from
  const write = (nextFrom: number | undefined, nextTo: number | undefined) => {
    setFrom(nextFrom)
    setTo(nextTo)
    const bad =
      nextFrom !== undefined && nextTo !== undefined && nextTo <= nextFrom
    if (!bad) onChange({ axisMin: nextFrom, axisMax: nextTo })
  }
  return (
    <div className="dq-field">
      <span>Value axis</span>
      <div className="dq-field__pair">
        <InputNumber
          size="small"
          aria-label="Axis from"
          placeholder="From: auto"
          value={from ?? null}
          onChange={(v) => write(typed(v), to)}
        />
        <InputNumber
          size="small"
          aria-label="Axis to"
          aria-invalid={invalid || undefined}
          status={invalid ? "error" : undefined}
          placeholder="To: auto"
          value={to ?? null}
          onChange={(v) => write(from, typed(v))}
        />
      </div>
      {invalid ? (
        <span className="dq-field__hint dq-field__error" role="alert">
          {AXIS_RANGE_ERROR}
        </span>
      ) : (
        <span className="dq-field__hint">
          In the chart&apos;s units: 30 on a percentage chart is 30%.
        </span>
      )}
    </div>
  )
}

const REPEAT_COLUMN_OPTIONS: { value: RepeatBy["columns"]; label: string }[] =
  [
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
  ]
/** The columns a new repeat starts with, as the sanitiser defaults them. */
const DEFAULT_REPEAT_COLUMNS: RepeatBy["columns"] = 3

/**
 * Small multiples for a bar, line or pie tile: the variable whose
 * categories each get a panel (only variables with categories are
 * offered), and how many columns the panels take. Setting or clearing the
 * variable runs the tile again, one run per panel, and drops the viewer's
 * choices on it, since a repeated tile shows no viewer controls. The
 * columns only change the layout, so they never run the tile.
 */
const RepeatSection: React.FC<{ tile: RepeatTile; pageId: string }> = ({
  tile,
  pageId,
}) => {
  const codebook = useDashboardStore((s) => s.codebook)
  const hasControls = useDashboardStore(
    (s) => s.tileControls[tile.id] !== undefined
  )
  const updateTile = useDashboardStore((s) => s.updateTile)
  const updateTileView = useDashboardStore((s) => s.updateTileView)
  const setTileControl = useDashboardStore((s) => s.setTileControl)
  const options = useMemo(
    () =>
      flattenCodebookForSelect(codebook ?? []).filter(
        (o) => getBoolChildLabels(codebook, o.value).length > 0
      ),
    [codebook]
  )
  const repeat = tile.repeatBy
  const count = repeat ? repeatCategories(codebook, repeat.variable).length : 0
  const unfiltered =
    tile.source.kind === "script" &&
    !tile.source.script.includes(UNIVERSE_TOKEN)

  const setVariable = (variable: string | undefined): void => {
    updateTile(pageId, tile.id, (t) => {
      if (!canRepeat(t)) return t
      if (!variable) {
        const next = { ...t }
        delete next.repeatBy
        return next
      }
      return {
        ...t,
        repeatBy: {
          variable,
          columns: t.repeatBy?.columns ?? DEFAULT_REPEAT_COLUMNS,
        },
      }
    })
    if (variable && hasControls) {
      setTileControl(tile.id, {
        statistic: undefined,
        breakdown: undefined,
        categories: undefined,
      })
    }
  }
  const setColumns = (columns: RepeatBy["columns"]): void =>
    updateTileView(pageId, tile.id, (t) =>
      canRepeat(t) && t.repeatBy
        ? { ...t, repeatBy: { ...t.repeatBy, columns } }
        : t
    )

  return (
    <>
      <h4 className="dq-inspector__h">Small multiples</h4>
      <div className="dq-toggle">
        <label className="dq-field">
          <span>Repeat by</span>
          <Select
            size="small"
            allowClear
            showSearch
            placeholder="None"
            value={repeat?.variable}
            options={options}
            onChange={(v) => setVariable(v ?? undefined)}
          />
        </label>
        {count > MAX_PANELS && (
          <p className="dq-field__hint">
            Shows the first {MAX_PANELS} of {count} categories.
          </p>
        )}
        {repeat && unfiltered && (
          <p className="dq-field__hint">
            Add {UNIVERSE_TOKEN} to the script so each panel shows its own
            category.
          </p>
        )}
      </div>
      {repeat && (
        <div className="dq-field">
          <span>Columns</span>
          <Radio.Group
            size="small"
            optionType="button"
            buttonStyle="solid"
            className="dq-field__choice"
            value={repeat.columns}
            options={REPEAT_COLUMN_OPTIONS}
            onChange={(e) => setColumns(e.target.value)}
          />
        </div>
      )}
    </>
  )
}

const statisticLabel = (stat: STATISTIC_TYPE): string =>
  [...XTAB_STATISTIC_OPTIONS, ...DTAB_STATISTIC_OPTIONS].find(
    (o) => o.value === stat
  )?.label ?? stat

/**
 * The statistics a chart or KPI can draw. A builder tile offers only what it
 * computes: its statistics, plus the tested ones while significance is on.
 * A script tile offers the whole list. The current choice always stays
 * listed, marked when a builder tile does not compute it.
 */
const statisticOptions = (
  tile: DataTile,
  current: STATISTIC_TYPE
): { value: STATISTIC_TYPE; label: string }[] => {
  const src = tile.source
  const all =
    src.kind === "builder" && src.operation === TABLE_OPERATION_TYPE.DTAB
      ? DTAB_STATISTIC_OPTIONS
      : XTAB_STATISTIC_OPTIONS
  if (src.kind !== "builder") {
    return all.some((o) => o.value === current)
      ? all
      : [...all, { value: current, label: statisticLabel(current) }]
  }
  const computed = new Set<STATISTIC_TYPE>([
    ...src.statistics,
    ...(src.significance?.enabled
      ? SIGNIFICANCE_TESTED_STATISTICS[src.operation]
      : []),
  ])
  const options = all.filter((o) => computed.has(o.value))
  return options.some((o) => o.value === current)
    ? options
    : [
        ...options,
        { value: current, label: `${statisticLabel(current)} (not computed)` },
      ]
}

/**
 * How a tile draws: its title, then the settings of its type (chart shape,
 * statistic, a bar's hidden columns, table style, KPI cell, scorecard,
 * image fit), a chart's options, small multiples for charts, and the
 * category editor for charts and tables, the one place a row is hidden.
 * The category lists come from the tile's last result, not its view, and
 * the row lists add the tile's measures. The title, a bar's hidden
 * columns, a table's style and font scale, a KPI's row and column, chart
 * options, the scorecard and the category editor only change how the last
 * result is drawn, so they never run the tile; the other settings go
 * through `updateTile`.
 */
const DisplayTab: React.FC<{ tile: Tile; pageId: string }> = ({
  tile,
  pageId,
}) => {
  const updateTile = useDashboardStore((s) => s.updateTile)
  const updateTileView = useDashboardStore((s) => s.updateTileView)
  const theme = useDashboardStore((s) => s.document.theme.tableStyle)
  // A repeated tile lists from its first panel: every panel has the same
  // rows and columns.
  const model = useDashboardStore((s) => runtimeModel(s.tiles[tile.id]))
  const set = (fn: (t: Tile) => Tile): void => updateTile(pageId, tile.id, fn)
  // Chart options only change how the last result is drawn: no new run.
  const setChart = (patch: Partial<ChartOptions>): void =>
    updateTileView(pageId, tile.id, (t) =>
      isDataTile(t) ? { ...t, chart: patchChart(t.chart, patch) } : t
    )
  // A scorecard reads its view: its choices never run the tile either.
  const setScorecard = (patch: Partial<ScorecardTile["visual"]>): void =>
    updateTileView(pageId, tile.id, (t) =>
      t.type === "scorecard"
        ? { ...t, visual: patchScorecard(t.visual, patch) }
        : t
    )
  // What only changes how the last result is drawn: no new run.
  const setView = (fn: (t: Tile) => Tile): void =>
    updateTileView(pageId, tile.id, fn)
  const patchVisual =
    <K extends DataTile["type"]>(type: K, patch: Partial<VisualOf<K>>) =>
    (t: Tile): Tile =>
      t.type === type && isDataTile(t)
        ? ({ ...t, visual: { ...t.visual, ...patch } } as Tile)
        : t
  const setVisual = <K extends DataTile["type"]>(
    type: K,
    patch: Partial<VisualOf<K>>
  ): void => set(patchVisual(type, patch))
  const setVisualView = <K extends DataTile["type"]>(
    type: K,
    patch: Partial<VisualOf<K>>
  ): void => setView(patchVisual(type, patch))
  const builder = isDataTile(tile) && tile.source.kind === "builder"
  const downLeaves = categoryLeaves(model?.down.leaves)
  const acrossLeaves = categoryLeaves(model?.across.leaves)
  const downOptions = isDataTile(tile)
    ? downRowOptions(tile, model)
    : leafOptions(downLeaves)
  const totalsColumn = model?.across.leaves.find((l) => l.isTotal)
  const columnOptions = [
    ...leafOptions(acrossLeaves),
    ...(totalsColumn ? leafOptions([totalsColumn]) : []),
  ]
  const showsRows =
    isDataTile(tile) &&
    (tile.type === "bar" ||
      tile.type === "line" ||
      tile.type === "pie" ||
      tile.type === "table")

  return (
    <div className="dq-inspector__section">
      <label className="dq-field">
        <span>Title</span>
        <Input
          size="small"
          value={tile.title ?? ""}
          placeholder="Untitled"
          onChange={(e) =>
            setView((t) => ({ ...t, title: e.target.value || undefined }))
          }
        />
      </label>

      {tile.type === "bar" && (
        <>
          <label className="dq-field">
            <span>Orientation</span>
            <Select
              size="small"
              value={tile.visual.orientation}
              options={ORIENTATION_OPTIONS}
              onChange={(orientation) => setVisual("bar", { orientation })}
            />
          </label>
          <label className="dq-field">
            <span>Bars</span>
            <Select
              size="small"
              value={tile.visual.mode}
              options={BAR_MODE_OPTIONS}
              onChange={(mode) => setVisual("bar", { mode })}
            />
          </label>
          <label className="dq-field">
            <span>Statistic</span>
            <Select
              size="small"
              value={tile.visual.statistic}
              options={statisticOptions(tile, tile.visual.statistic)}
              onChange={(statistic) => setVisual("bar", { statistic })}
            />
          </label>
          <label className="dq-field">
            <span>Series from</span>
            <Select
              size="small"
              value={tile.visual.seriesAxis}
              options={SERIES_AXIS_OPTIONS}
              onChange={(seriesAxis) => setVisual("bar", { seriesAxis })}
            />
          </label>
          <label className="dq-field dq-field--row">
            <span>Show totals</span>
            <Switch
              size="small"
              checked={tile.visual.showTotals}
              onChange={(showTotals) => setVisual("bar", { showTotals })}
            />
          </label>
          {/* With the series from the rows, the columns are the categories:
              the category editor hides rows only, so the columns have this. */}
          {tile.visual.seriesAxis === "down" && (
            <label className="dq-field">
              <span>Hide columns</span>
              <Select
                size="small"
                mode="multiple"
                value={tile.visual.hiddenCategories}
                options={leafOptions(acrossLeaves)}
                placeholder="None"
                onChange={(hiddenCategories) =>
                  setVisualView("bar", { hiddenCategories })
                }
              />
            </label>
          )}
        </>
      )}

      {tile.type === "line" && (
        <>
          <label className="dq-field">
            <span>Statistic</span>
            <Select
              size="small"
              value={tile.visual.statistic}
              options={statisticOptions(tile, tile.visual.statistic)}
              onChange={(statistic) => setVisual("line", { statistic })}
            />
          </label>
          <label className="dq-field">
            <span>Wave variable</span>
            <Select
              size="small"
              allowClear
              value={tile.visual.waveVariable || undefined}
              placeholder="First column variable"
              options={
                tile.source.kind === "builder"
                  ? tile.source.columns.map((c) => ({ value: c, label: c }))
                  : []
              }
              onChange={(waveVariable) =>
                setVisual("line", { waveVariable: waveVariable ?? "" })
              }
            />
          </label>
          <label className="dq-field dq-field--row">
            <span>Show points</span>
            <Switch
              size="small"
              checked={tile.visual.showPoints}
              onChange={(showPoints) => setVisual("line", { showPoints })}
            />
          </label>
          <label className="dq-field">
            <span>Hide series</span>
            <Select
              size="small"
              mode="multiple"
              value={tile.visual.hiddenSeries}
              options={downOptions}
              onChange={(hiddenSeries) => setVisual("line", { hiddenSeries })}
            />
          </label>
        </>
      )}

      {tile.type === "pie" && (
        <>
          <label className="dq-field">
            <span>Statistic</span>
            <Select
              size="small"
              value={tile.visual.statistic}
              options={statisticOptions(tile, tile.visual.statistic)}
              onChange={(statistic) => setVisual("pie", { statistic })}
            />
          </label>
          <label className="dq-field dq-field--row">
            <span>Donut</span>
            <Switch
              size="small"
              checked={tile.visual.donut}
              onChange={(donut) => setVisual("pie", { donut })}
            />
          </label>
          <label className="dq-field">
            <span>Legend</span>
            <Select
              size="small"
              value={tile.visual.legend}
              options={LEGEND_OPTIONS}
              onChange={(legend) => setVisual("pie", { legend })}
            />
          </label>
        </>
      )}

      {tile.type === "table" && (
        <>
          {/* Not <label>: the control holds a picker and a button. */}
          <div className="dq-field">
            <span>Table style</span>
            <TableStyleControl
              value={tile.visual.tableStyle}
              onChange={(tableStyle) => setVisualView("table", { tableStyle })}
              allowInherit
              inheritLabel="Dashboard default"
              inherited={theme ?? DASHBOARD_TABLE_STYLE}
              drawerTitle="Table style: this tile"
            />
          </div>
          <label className="dq-field dq-field--row">
            <span>Transpose</span>
            <Switch
              size="small"
              checked={tile.visual.transposed}
              disabled={!builder}
              onChange={(transposed) => setVisual("table", { transposed })}
            />
          </label>
          {!builder && (
            <p className="dq-field__hint">
              A script tile transposes in its script: add /T to its OUTPUT
              line.
            </p>
          )}
          <label className="dq-field">
            <span>Font scale</span>
            <InputNumber
              size="small"
              min={0.6}
              max={1.6}
              step={0.1}
              value={tile.visual.fontScale ?? 1}
              onChange={(v) =>
                setVisualView("table", { fontScale: v ?? undefined })
              }
            />
          </label>
        </>
      )}

      {tile.type === "kpi" && (
        <>
          <label className="dq-field">
            <span>Row</span>
            <Select
              size="small"
              allowClear
              value={tile.visual.row ?? undefined}
              placeholder="First row"
              options={downOptions}
              onChange={(row) => setVisualView("kpi", { row: row ?? null })}
            />
          </label>
          <label className="dq-field">
            <span>Column</span>
            <Select
              size="small"
              allowClear
              value={tile.visual.column ?? undefined}
              placeholder="Totals"
              options={leafOptions(acrossLeaves)}
              onChange={(column) =>
                setVisualView("kpi", { column: column ?? null })
              }
            />
          </label>
          <label className="dq-field">
            <span>Statistic</span>
            <Select
              size="small"
              value={tile.visual.statistic}
              options={statisticOptions(tile, tile.visual.statistic)}
              onChange={(statistic) => setVisual("kpi", { statistic })}
            />
          </label>
          <label className="dq-field dq-field--row">
            <span>Show base</span>
            <Switch
              size="small"
              checked={tile.visual.showBase}
              onChange={(showBase) => setVisual("kpi", { showBase })}
            />
          </label>
          <label className="dq-field">
            <span>Decimals</span>
            <InputNumber
              size="small"
              min={0}
              max={4}
              precision={0}
              value={tile.visual.decimals}
              onChange={(v) => setVisual("kpi", { decimals: v ?? undefined })}
            />
          </label>
        </>
      )}

      {tile.type === "scorecard" && (
        <>
          <label className="dq-field">
            <span>Source</span>
            <Select
              size="small"
              allowClear
              value={tile.visual.source || undefined}
              placeholder="First row"
              options={downOptions}
              onChange={(source) => setScorecard({ source: source ?? "" })}
            />
          </label>
          <label className="dq-field">
            <span>Column</span>
            <Select
              size="small"
              allowClear
              value={tile.visual.column ?? undefined}
              placeholder="Latest"
              options={columnOptions}
              onChange={(column) => setScorecard({ column: column ?? null })}
            />
          </label>
          <div className="dq-field">
            <span>Compare with</span>
            <Radio.Group
              size="small"
              optionType="button"
              buttonStyle="solid"
              className="dq-field__choice"
              value={tile.visual.compare}
              options={COMPARE_OPTIONS}
              onChange={(e) => setScorecard({ compare: e.target.value })}
            />
          </div>
          {tile.visual.compare === "target" && (
            <label className="dq-field">
              <span>Target value</span>
              <InputNumber
                size="small"
                placeholder="None"
                value={tile.visual.target ?? null}
                onChange={(v) => setScorecard({ target: typed(v) })}
              />
              <span className="dq-field__hint">
                In the row&apos;s units: 20 on a percentage row is 20%.
              </span>
            </label>
          )}
          <div className="dq-field">
            <span>Good direction</span>
            <Radio.Group
              size="small"
              optionType="button"
              buttonStyle="solid"
              className="dq-field__choice"
              value={tile.visual.goodDirection}
              options={GOOD_OPTIONS}
              onChange={(e) => setScorecard({ goodDirection: e.target.value })}
            />
          </div>
          <label className="dq-field dq-field--row">
            <span>Show rank</span>
            <Switch
              size="small"
              checked={tile.visual.showRank}
              onChange={(showRank) => setScorecard({ showRank })}
            />
          </label>
          <label className="dq-field">
            <span>Decimals</span>
            <InputNumber
              size="small"
              min={0}
              max={4}
              precision={0}
              placeholder="As the table"
              value={tile.visual.decimals ?? null}
              onChange={(v) => setScorecard({ decimals: typed(v) })}
            />
          </label>
        </>
      )}

      {(tile.type === "bar" || tile.type === "line" || tile.type === "pie") && (
        <ChartSection tile={tile} onChange={setChart} />
      )}

      {canRepeat(tile) && <RepeatSection tile={tile} pageId={pageId} />}

      {showsRows && (
        <>
          <h4 className="dq-inspector__h">Categories</h4>
          {tile.type === "table" && tile.visual.transposed ? (
            <p className="dq-field__hint">
              A transposed table shows its rows as they come. Untick Transpose
              to order, hide or rename them.
            </p>
          ) : (
            <CategoryDisplayEditor key={tile.id} tile={tile} pageId={pageId} />
          )}
        </>
      )}

      {tile.type === "image" && (
        <label className="dq-field">
          <span>Fit</span>
          <Select
            size="small"
            value={tile.fit}
            options={FIT_OPTIONS}
            onChange={(fit) =>
              set((t) => (t.type === "image" ? { ...t, fit } : t))
            }
          />
        </label>
      )}

      {tile.type === "text" && (
        <p className="dq-field__hint">Double-click the tile to edit its text.</p>
      )}
    </div>
  )
}

export default DisplayTab
